// design-audit-color-file: the coin's gold gradient is brand art, kept as a reasoned survivor.
// The client portal's motion vocabulary, mirrored one-to-one from the Motion Bench
// (savvy-sandbox › prototypes/chloe/motion-bench › motion.jsx). Same names, same
// values, so a number tuned on the bench is a same-name edit here. Seconds unless
// a name says otherwise.

type CubicBezier = readonly [number, number, number, number];

type ChangeLevelId = 'still' | 'nudge' | 'small' | 'medium' | 'large' | 'major';

type ChangeLevel = {
  readonly id: ChangeLevelId;
  readonly upTo: number;
  readonly amp: number;
  readonly coins: number;
};

type MilestoneTierId = 'halfMillion' | 'million' | 'fiveMillion' | 'tenMillion';

type MilestoneTier = {
  readonly id: MilestoneTierId;
  readonly step: number;
  readonly coins: number;
  readonly lift: number;
  readonly squash: number;
  readonly duration: number;
};

type Emphasis = 'leading' | 'even' | 'trailing';

type CascadeOrigin = 'left' | 'right' | 'center';

// A reveal already this far along on its first painted frame: the viewer sees the last third play.
const MOUNT_AT = 0.66;
// A range switch travels the whole way from the shape on screen; starting part-way reads as a reload.
const SWITCH_AT = 0;

const DUR = 0.16;
const DUR_NUMBER = DUR;
// The slow knob: the curve is the only element moving through space rather than changing in place.
const DUR_CURVE = 0.85;

// The order a chart card resolves in: axis, curve, headline (with its gain), percentage last.
const DELAY_AXIS = 0;
const DELAY_CURVE = 0.14;
const DELAY_HEADLINE = 0.2;
const DELAY_GAIN = DELAY_HEADLINE;
const DELAY_PERCENT = DELAY_HEADLINE + 0.18;

// How long a figure is on its way down before it swaps to the new value. Milliseconds.
const DIP_MS = 110;

// How loudly a money figure reacts, keyed to the size of the move in dollars.
const CHANGE_LEVELS: readonly ChangeLevel[] = [
  { id: 'still', upTo: 1e4, amp: 0, coins: 0 },
  { id: 'nudge', upTo: 5e4, amp: 0.25, coins: 2 },
  { id: 'small', upTo: 1e5, amp: 0.42, coins: 3 },
  { id: 'medium', upTo: 5e5, amp: 0.62, coins: 4 },
  { id: 'large', upTo: 1e6, amp: 0.82, coins: 5 },
  { id: 'major', upTo: Infinity, amp: 1, coins: 6 },
];

// The same ladder as a proportion of the figure, for surfaces that grade on percent growth.
const CHANGE_LEVELS_RELATIVE: readonly ChangeLevel[] = [
  { id: 'still', upTo: 0.02, amp: 0, coins: 0 },
  { id: 'nudge', upTo: 0.05, amp: 0.25, coins: 2 },
  { id: 'small', upTo: 0.1, amp: 0.42, coins: 3 },
  { id: 'medium', upTo: 0.2, amp: 0.62, coins: 4 },
  { id: 'large', upTo: 0.35, amp: 0.82, coins: 5 },
  { id: 'major', upTo: Infinity, amp: 1, coins: 6 },
];

// Percentages graded by how many points they moved, which is what a reader compares.
const CHANGE_LEVELS_POINTS: readonly ChangeLevel[] = [
  { id: 'still', upTo: 0.25, amp: 0, coins: 0 },
  { id: 'nudge', upTo: 1, amp: 0.25, coins: 2 },
  { id: 'small', upTo: 3, amp: 0.42, coins: 3 },
  { id: 'medium', upTo: 10, amp: 0.62, coins: 4 },
  { id: 'large', upTo: 25, amp: 0.82, coins: 5 },
  { id: 'major', upTo: Infinity, amp: 1, coins: 6 },
];

// How far a figure may travel on first load, as a fraction of itself. A loss keeps four times the
// travel: money coming down is the thing worth watching. Sized so the resting roll sits under the
// quietest change level.
const FIRST_LOAD_TRAVEL = { gain: 0.0025, loss: 0.01 } as const;

// Where an achievement's climb starts, as a fraction of the round number it arrives at.
const ACHIEVEMENT_RUNUP = 0.94;

const INTENSITY_PRIMARY = 1;
const INTENSITY_HEADLINE = 0.7;
const INTENSITY_SECONDARY = 0.7;
const INTENSITY_PERCENT = 0.8;

// Supporting stats wait for the headline row, percentage included, then sweep left to right.
const DELAY_SUPPORTING = DELAY_PERCENT + 1.0;
const DELAY_STAT_STEP = 0.1;
const DUR_STAT = 0.6;
// The side panel is the last thing on the page to arrive. It waits out the supporting row's whole sweep —
// three cells stepped by DELAY_STAT_STEP — so the reader finishes the card they were watching before
// anything asks for their attention beside it.
const DELAY_PANEL = DELAY_SUPPORTING + 3 * DELAY_STAT_STEP;
const DELAY_ALLOCATION = 0.34;

// The ring breathing out of a status badge. Overdue breathes faster and stronger.
const PULSE = {
  duration: 3.2,
  durationUrgent: 2.2,
  alpha: 0.22,
  intensityUrgent: 1.6,
  scale: [1, 1.75, 1.95] as const,
  tone: 'var(--secondary)',
  ease: [0, 0, 0.58, 1] as const satisfies CubicBezier,
  repeatDelay: 0.4,
} as const;

// A coin in flight, and the spray a milestone throws.
const COIN = {
  flight: 0.9,
  bankFlight: 0.68,
  bankEase: [0.16, 1, 0.3, 1] as const satisfies CubicBezier,
  size: 13,
  sizeSmall: 9,
  edgesDefault: 2,
  edgesSmall: 4,
} as const;

// Figure choreography, in em rather than pixels, so one set of numbers is right at every type size
// and `maxEm` is a ceiling no combination of span, intensity and tier can exceed.
const FIGURE = {
  dipBase: 0.26,
  dipPerSpan: 0.2,
  riseBase: 0.21,
  risePerSpan: 0.17,
  maxEm: 0.5,
  bounce: 0.34,
  origin: 'left' satisfies CascadeOrigin,
  taper: 0.72,
  // How much more a large change moves than a small one, across every channel at once.
  gradient: 1.4,
  ease: [0, 0, 0.58, 1] as const satisfies CubicBezier,
  charStagger: 0.016,
  // A short string needs more offset per character to read as digits settling, not less.
  charStaggerPercent: 0.028,
  downward: 0.1,
  tierDip: 1.5,
  tierRise: 1.6,
} as const;

type FigureTokens = {
  readonly dipBase: number;
  readonly dipPerSpan: number;
  readonly riseBase: number;
  readonly risePerSpan: number;
  readonly maxEm: number;
  readonly bounce: number;
  readonly origin: CascadeOrigin;
  readonly taper: number;
  readonly gradient: number;
  readonly ease: CubicBezier;
  readonly charStagger: number;
  readonly charStaggerPercent: number;
  readonly downward: number;
  readonly tierDip: number;
  readonly tierRise: number;
};

// A money figure whose left-hand digits carry the meaning: one lift led from the left.
const LEAD_FIGURE: Partial<FigureTokens> = { charStagger: 0, taper: 0.9 };

// Axis label swap: out fast, hold while the scale changes, back in slowly. Milliseconds.
const AXIS = { out: 170, in: 420 } as const;

const EASE_SETTLE = 'easeOut';
// The one curve every graph animation uses: gathers, then decelerates into the landing.
const EASE_GRAPH: CubicBezier = [0.4, 0, 0.2, 1];
const EASE_SWITCH = 'easeInOut';
const COLLAPSE = { duration: 0.36, ease: [0.22, 1, 0.36, 1] as const satisfies CubicBezier } as const;
// A figure never uses EASE_GRAPH: gains ease in and arrive with momentum, losses ease off and settle.
const EASE_GAIN: CubicBezier = [0.42, 0, 1, 1];
const EASE_LOSS: CubicBezier = [0, 0, 0.58, 1];
// A segmented bar is revealed, never resized: springing five sibling widths never sums to 100%.
const ALLOCATION_FILL = { duration: 0.72, ease: EASE_GRAPH } as const;
const SPRING_POP = { type: 'spring', stiffness: 220, damping: 16 } as const;
const EMPHASIS: Emphasis = 'even';

// Round numbers people notice crossing. Precedence is highest first, so $5M reads as $5M and not
// as another million.
const MILESTONE_TIERS: Readonly<Record<MilestoneTierId, MilestoneTier>> = {
  tenMillion: { id: 'tenMillion', step: 1e7, coins: 12, lift: 26, squash: 1.26, duration: 1.15 },
  fiveMillion: { id: 'fiveMillion', step: 5e6, coins: 9, lift: 21, squash: 1.2, duration: 1.0 },
  million: { id: 'million', step: 1e6, coins: 7, lift: 17, squash: 1.15, duration: 0.86 },
  halfMillion: { id: 'halfMillion', step: 5e5, coins: 4, lift: 12, squash: 1.1, duration: 0.72 },
};

const COIN_SIZE_BY_TIER: Readonly<Record<MilestoneTierId, number>> = {
  halfMillion: 15,
  million: 17,
  fiveMillion: 20,
  tenMillion: 23,
};

// Gold in both colour modes on purpose: a coin is the same object everywhere it appears.
const COIN_FACE = {
  background: 'radial-gradient(circle at 34% 30%, #F6D97A, #D8A72C 68%, #B98A1E)',
  border: '1.5px solid #C79A28',
} as const;

// Vertices a curve is drawn with while it travels, so the point count is fixed for the whole
// transition; 90 still reads as smooth on a line this thin.
const MORPH_POINTS = 90;
const MORPH_SMOOTH = 1;

// The shell arriving after the splash: the navigation settles first and the surface follows it in, so the
// frame is established before anything inside it moves. One beat apart — enough to read as an order rather
// than a stagger, not enough to feel like waiting.
const SHELL_IN = {
  duration: 0.4,
  navDelay: 0,
  surfaceDelay: 0.18,
  ease: [0.16, 1, 0.3, 1] as const satisfies CubicBezier,
} as const;

// A phone tab icon acknowledging the tap: a quick over-and-back scale, on the settle curve.
const TAB_POP = {
  duration: 0.28,
  scale: [1, 1.18, 0.95, 1] as const,
  times: [0, 0.4, 0.7, 1] as const,
  ease: [0.22, 1, 0.36, 1] as const satisfies CubicBezier,
} as const;

// The cross-fade from a page of skeletons onto real content. Long enough to read as one surface resolving
// rather than a swap, short enough that the choreography behind it is not left waiting.
const REVEAL = {
  duration: 0.35,
  ease: [0.16, 1, 0.3, 1] as const satisfies CubicBezier,
} as const;

const SHIMMER = {
  duration: 4.2,
  repeatDelay: 0.7,
  delay: 0.5,
  width: '130%',
  alpha: 0.38,
  alphaEdge: 0.16,
  ease: [0.42, 0, 0.58, 1] as const satisfies CubicBezier,
} as const;

const LINE_DRAW = {
  duration: 0.95,
  ease: [0.33, 1, 0.68, 1] as const satisfies CubicBezier,
  delay: 0,
} as const;

const PROGRESS = {
  duration: 0.42,
  ease: [0.16, 1, 0.3, 1] as const satisfies CubicBezier,
  floor: 8,
} as const;

const TICK = {
  pop: 0.52,
  popEase: [0.22, 1.4, 0.4, 1] as const,
  peak: 1.22,
  drawDelay: 0.09,
  draw: 0.42,
  drawEase: [0.4, 0, 0.2, 1] as const satisfies CubicBezier,
  strikeDelay: 0.22,
  strike: 0.52,
} as const;

// The band of light that says "being worked out": the text that shimmers while Savvy Intelligence calculates and the
// listening indicator run on it, so the two read as the same wait. `spread` is the band's width in characters.
const SHIMMER_MOTION = {
  duration: 1.5,
  spread: 2,
} as const;

const SKELETON_MOTION = {
  shimmer: 1.4,
  ease: [0.42, 0, 0.58, 1] as const satisfies CubicBezier,
  alpha: 0.6,
  rowGap: 0.07,
} as const;

const LOAD_IN_MOTION = {
  duration: 0.36,
  // A piece arriving into a panel the reader is already working in: present, not performed.
  quickDuration: 0.18,
  ease: [0.22, 1, 0.36, 1] as const satisfies CubicBezier,
  stagger: 0.045,
  maxStagger: 8,
  lift: 8,
} as const;

const SETTLE_MOTION = {
  duration: 0.32,
  lift: 4,
  ease: [0.16, 1, 0.3, 1] as const satisfies CubicBezier,
  refreshOpacity: 0.5,
  refreshDuration: 0.2,
  swap: 0.2,
} as const;

// Graphs that arrive together open one step apart, and the wave stops growing after `max` of them: a grid of twenty
// charts would otherwise still be opening seconds after it was asked for.
const GRAPH_ARRIVAL = { step: 0.12, max: 6 } as const;

const RESIZE_MOTION = {
  duration: 0.7,
  ease: [0.4, 0, 0.2, 1] as const satisfies CubicBezier,
} as const;

// How the reward answers a coin landing, a section finishing, a nudge and the finale. The name is the bench's
// (it was tuned on a pig), kept so a bench edit is a same-name edit here.
const PIGGY = {
  squash: 0.38,
  squashPop: 1.14,
  squashLift: 5,
  squashEase: [0, 0, 0.58, 1] as const satisfies CubicBezier,
  cheer: 0.72,
  cheerRotate: 12,
  cheerPop: 1.18,
  cheerEase: [0.42, 0, 0.58, 1] as const satisfies CubicBezier,
  wiggle: 0.7,
  wiggleRotate: 14,
  wiggleEase: [0.42, 0, 0.58, 1] as const satisfies CubicBezier,
  finale: 1.15,
  finaleRotate: 13,
  finaleLift: 11,
  finaleEase: [0.42, 0, 0.58, 1] as const satisfies CubicBezier,
  pop: 0.62,
  popCoins: 6,
  popSpread: 34,
  microCoins: 4,
  microSpread: 24,
  arriveGap: 0.6,
  countUp: 0.35,
} as const;

const SCENE = {
  seed: 0.28,
  grow: { type: 'spring', stiffness: 420, damping: 15 },
  sway: 0.9,
  climb: 0.55,
  climbDelay: 0.16,
  climbHop: 5,
  wave: 0.8,
} as const;

// The globe, seen from `tilt`° north and turning east once every `period` seconds from `startLon` (the Atlantic,
// with the Americas, Europe and Africa in view), redrawn `fps` times a second.
const GLOBE_SPIN = { period: 26, tilt: 20, startLon: -40, fps: 24 } as const;

// The plane's orbit round it, an ellipse `tilt`° off level: one lap every `period` seconds, a landing's extra lap
// paid out at `payout` a second, the plane drawn at `planeScale` in front (any smaller and it reads as a speck),
// smaller and fainter round the back, and a fading trail of `trail` dots behind it. The bench calls this ORBIT.
const GLOBE_ORBIT = {
  rx: 27,
  ry: 8.5,
  tilt: -14,
  period: 7,
  payout: 3.2,
  planeScale: 0.95,
  farScale: 0.62,
  farOpacity: 0.7,
  trail: 5,
  trailStep: 0.15,
  trailOpacity: 0.4,
} as const;

// One completion mark everywhere. 28px: at 24 a glyph inside the same-sized circle was too small to read.
const DONE_MARK = { size: 28, tick: 16, weight: 2.8 } as const;

// The bench's --brand-fill, which the kit has no variable for: brand green into mint. --success is the same green
// as --primary, so it cannot be the ramp's far end.
const MOMENTUM_GRADIENT = 'linear-gradient(90deg, var(--primary) 0%, var(--primary-outline) 100%)';

// A slow, calm fade in and out that loops while content is regenerated (4s per full breath): almost white up to a
// light grey at its darkest, never solid, linear so it is always moving.
const REGENERATING_BREATHE = {
  duration: 4,
  min: 0.1,
  max: 0.3,
  ease: [0, 0, 1, 1],
} as const satisfies { ease: CubicBezier } & Record<string, unknown>;

// A conversation turn: the sent message lifts in, the working row/answer/footer fade in a beat apart, and a status
// label rolls to its next words a letter at a time rather than cutting.
const CONVERSATION_MOTION = {
  send: { duration: 0.22, lift: 10, ease: [0.22, 1, 0.36, 1] },
  reveal: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
  footerDelay: 0.12,
  wordSwap: { duration: 0.55, exit: 0.3, stagger: 0.022, maxSpread: 0.28, lift: 7, blur: 4, ease: [0.22, 1, 0.36, 1] },
} as const satisfies Record<string, unknown>;

// A step in a vertical journey unfolding open: its height and content fade in together, a beat after the tick lands.
const UNFOLD = { fade: 0.22, height: 0.42, delay: 0.22, ease: EASE_SETTLE } as const;

export {
  ACHIEVEMENT_RUNUP,
  CONVERSATION_MOTION,
  REGENERATING_BREATHE,
  UNFOLD,
  ALLOCATION_FILL,
  AXIS,
  CHANGE_LEVELS,
  CHANGE_LEVELS_POINTS,
  CHANGE_LEVELS_RELATIVE,
  COIN,
  COIN_FACE,
  COIN_SIZE_BY_TIER,
  COLLAPSE,
  DELAY_ALLOCATION,
  DELAY_AXIS,
  DELAY_CURVE,
  DELAY_GAIN,
  DELAY_HEADLINE,
  DELAY_PANEL,
  DELAY_PERCENT,
  DELAY_STAT_STEP,
  DELAY_SUPPORTING,
  DIP_MS,
  DONE_MARK,
  DUR,
  DUR_CURVE,
  DUR_NUMBER,
  DUR_STAT,
  EASE_GAIN,
  EASE_GRAPH,
  EASE_LOSS,
  EASE_SETTLE,
  EASE_SWITCH,
  EMPHASIS,
  FIGURE,
  FIRST_LOAD_TRAVEL,
  GLOBE_ORBIT,
  GLOBE_SPIN,
  GRAPH_ARRIVAL,
  INTENSITY_HEADLINE,
  INTENSITY_PERCENT,
  INTENSITY_PRIMARY,
  INTENSITY_SECONDARY,
  LEAD_FIGURE,
  LINE_DRAW,
  LOAD_IN_MOTION,
  MILESTONE_TIERS,
  MOMENTUM_GRADIENT,
  MORPH_POINTS,
  MORPH_SMOOTH,
  MOUNT_AT,
  PIGGY,
  PROGRESS,
  PULSE,
  REVEAL,
  RESIZE_MOTION,
  SCENE,
  SETTLE_MOTION,
  SHELL_IN,
  SHIMMER,
  SHIMMER_MOTION,
  SKELETON_MOTION,
  SPRING_POP,
  SWITCH_AT,
  TAB_POP,
  TICK,
};
export type {
  CascadeOrigin,
  ChangeLevel,
  ChangeLevelId,
  CubicBezier,
  Emphasis,
  FigureTokens,
  MilestoneTier,
  MilestoneTierId,
};
